import React from 'react';

import { AddContactForm } from './form';
import { AddContactInputs } from './contact_inputs';
import { Button } from 'components/ui/button';
import { ContactRoleInput } from './role_input';
import { Errors } from 'components/ui/errors';
import { ForRecordInput } from './for_record_input';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { VisibilityInput } from './visibility_input';
import { useAddContactWorkflow } from './workflow';

import type { ForRecordOptions, NewContactRole } from './types';
import type { Role, Visibility } from 'bundles/backoffice/contacts/types';

interface Props {
  allowTagging?: boolean;
  contactLabel?: string;
  forRecordOptions?: ForRecordOptions;
  roleOptions?: Role[];
  show: boolean;
  showQuickbooks?: boolean;
  visibilityOptions?: Visibility[];
  onAdd: (contactRole: NewContactRole) => unknown;
  onClose: () => void;
}

export function AddContactModal({
  allowTagging,
  contactLabel = 'contact',
  forRecordOptions,
  roleOptions,
  show,
  showQuickbooks,
  visibilityOptions,
  onAdd,
  onClose,
}: Props) {
  const workflow = useAddContactWorkflow({ forRecordOptions, roleOptions, visibilityOptions, onComplete: onAdd });

  async function save() {
    if (await workflow.save()) {
      close();
    }
  }

  function close() {
    workflow.reset();
    onClose();
  }

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered>
        <Modal.Header>
          <Modal.Title>Add {contactLabel}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Errors className="mb-3" errors={workflow.errors} />

          <AddContactForm workflow={workflow}>
            <AddContactInputs allowTagging={allowTagging} showQuickbooks={showQuickbooks} workflow={workflow} />
            <ContactRoleInput
              role={workflow.role}
              roleOptions={workflow.roleOptions}
              onChange={role => workflow.change({ role })}
            />
            <VisibilityInput workflow={workflow} />
            <ForRecordInput workflow={workflow} />
          </AddContactForm>
        </Modal.Body>

        <Modal.Footer>
          <Button label="Cancel" variant="ghost" onClick={close} />
          <Button
            disabled={!workflow.canSave}
            label={workflow.step === 'form' ? `Add ${contactLabel}` : 'Confirm'}
            onClick={save}
          />
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
